import {
  CalendarIcon,
  ArrowRightIcon,
  MoonIcon,
  UserIcon,
  BuildingOffice2Icon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'hotel/navigation';

import { RequestStatusBadge } from 'components/Badge';
import { PrimaryButton, RedButton } from 'components/Button';
import {
  RosterRequestDataNewGroupReservation,
  RosterRequestStatus,
  useCancelRosterRequestMutation,
} from 'generated/graphql';
import { useAppSelector } from 'store/store';
import { calculateHotelNights } from 'utilities/calculateHotelNights';
import { popToast } from 'utilities/popToast';

import RoomDetailsSection from './RoomDetailsSection';

const YourGroupRequestCard = ({
  requestData,
  requestStatus,
  requestId,
}: {
  requestData: RosterRequestDataNewGroupReservation;
  requestStatus: RosterRequestStatus;
  requestId: string;
}) => {
  const { shortList, quantities, guestQuantity, checkInDate, checkOutDate } =
    requestData;
  const navigate = useNavigate();
  const roomConfigurationsFromRedux = useAppSelector(
    (state) => state.groupDetails.roomConfigurations,
  );

  // Use room configurations from requestData if available (viewing existing request),
  // otherwise fall back to Redux store (creating new request)
  const roomConfigurations =
    requestData.roomConfigurations || roomConfigurationsFromRedux;

  // Check if cancellation was requested based on status
  const isCancellationRequested =
    requestStatus === RosterRequestStatus.CancellationRequested;

  const numSelected = shortList.length;
  const numOfNights = calculateHotelNights(checkInDate, checkOutDate);

  const [cancelRosterRequest, { loading: cancelLoading }] =
    useCancelRosterRequestMutation({
      onCompleted: (data) => {
        popToast(
          `Cancellation request sent for request ${data.cancelRosterRequest.id}!`,
        );
      },
      onError: (error) => {
        popToast(`Error submitting request: ${error.message}`, {
          type: 'error',
        });
      },
    });

  return (
    <div className="space-y-4">
      {/* Cancellation Requested Alert */}
      {isCancellationRequested && (
        <div
          className="overflow-hidden rounded-lg border border-amber-200 bg-amber-50 shadow-sm"
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-start gap-3 p-4">
            <ExclamationTriangleIcon
              className="size-5 shrink-0 text-amber-600"
              aria-hidden="true"
            />
            <div>
              <p className="font-oswald text-lg font-bold text-amber-900">
                You requested to cancel this request
              </p>
              <p className="mt-1 text-sm text-amber-700">
                Your cancellation request has been sent to the THS team. They
                will update this request.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Card */}
      <div className="overflow-hidden rounded-lg border border-backgroundDarkGray bg-white shadow-sm">
        <div className="space-y-10 p-4 sm:p-6">
          <div>
            <p className="mb-4 font-oswald text-lg font-bold text-gray-900">
              Your Group Request
            </p>
            <div className="mb-4 border-b border-backgroundDarkGray"></div>
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-sm font-medium text-gray-500">Status</p>
                <RequestStatusBadge status={requestStatus} size="md" />
              </div>

              {requestStatus !== RosterRequestStatus.Confirmed && (
                <div>
                  <p className="mb-2 text-sm font-medium text-gray-500">
                    Hotels Selected ({numSelected})
                  </p>
                  <div className="space-y-3">
                    {shortList.map((hotel) => (
                      <div
                        key={hotel.amadeusHotelId}
                        className="flex items-start gap-2"
                      >
                        <BuildingOffice2Icon className="mt-0.5 size-5 shrink-0 text-accentGreen" />
                        <p className="font-arimo text-base font-medium capitalize text-gray-900">
                          {hotel.name.toLowerCase()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="mb-2 text-sm font-medium text-gray-500">Dates</p>
                <div className="flex items-center gap-2">
                  <CalendarIcon className="size-5 text-accentGreen" />
                  <p className="font-arimo text-base font-medium text-gray-900">
                    {checkInDate}
                  </p>
                  <ArrowRightIcon className="size-5 text-gray-900" />
                  <p className="font-arimo text-base font-medium text-gray-900">
                    {checkOutDate}
                  </p>
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-gray-500">
                  Total nights
                </p>
                <div className="flex items-center gap-2">
                  <MoonIcon className="size-5 text-accentGreen" />
                  <p className="font-arimo text-base font-medium text-gray-900">
                    {numOfNights}
                  </p>
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-gray-500">Guests</p>
                <div className="flex items-center gap-2">
                  <UserIcon className="size-5 text-accentGreen" />
                  <p className="font-arimo text-base font-medium text-gray-900">
                    {guestQuantity}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <p className="mb-4 font-oswald text-lg font-bold text-gray-900">
              Room Details
            </p>
            <div className="mb-4 border-b border-backgroundDarkGray"></div>
            <div className="space-y-4">
              <RoomDetailsSection
                roomType="single"
                roomTypeLabel="Single"
                configurations={roomConfigurations?.single}
                quantity={quantities.single}
              />

              <RoomDetailsSection
                roomType="double"
                roomTypeLabel="Double"
                configurations={roomConfigurations?.double}
                quantity={quantities.double}
              />

              <RoomDetailsSection
                roomType="suite"
                roomTypeLabel="Suite"
                configurations={roomConfigurations?.suite}
                quantity={quantities.suite}
              />
            </div>
          </div>
          {requestStatus == RosterRequestStatus.Confirmed && (
            <PrimaryButton
              className="w-full"
              onClick={() =>
                navigate('/trips?type=groupRequests&status=confirmed')
              }
            >
              View trip
            </PrimaryButton>
          )}

          {requestStatus === RosterRequestStatus.RequestReceived && (
            <div className="mt-6">
              <RedButton
                className="w-full"
                onClick={() =>
                  cancelRosterRequest({ variables: { id: requestId } })
                }
                disabled={cancelLoading || isCancellationRequested}
              >
                {cancelLoading ? 'Requesting...' : 'Request cancellation'}
              </RedButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default YourGroupRequestCard;
